import { useCallback, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api.js';
import { toast } from './Toasts.jsx';

// T415 — one "what is scheduled", read by both places that show it.
//
// There were two: UpcomingCard on the dashboard (30 days, read-only) and a
// Scheduled section on Tasks (60 days, with a Clear button). Same endpoint,
// same rows, two windows — so the same interview could be on one screen and
// not the other, and neither number could be trusted against the other.
// Every part was duplicated: KIND_LABEL, relativeDay, timeOf, the
// overdue-first sort and the row markup.
//
// One window (60 days) because a shorter one hides work rather than
// summarising it; the dashboard shows the first few and links here for the
// rest. Overdue first, always, in both — it is the state a job search
// actually loses applications in: a call happened, nothing moved, and the
// row looks identical to one that is merely waiting.

const KIND_LABEL = {
  interview: 'Interview',
  screening: 'Screening',
  call: 'Call',
  task: 'Task',
  follow_up: 'Follow up',
  deadline: 'Deadline',
  other: 'Event',
};

/** Human distance in days. Exported for tests — the arithmetic is the feature. */
export function relativeDay(at, now = new Date()) {
  const d = new Date(at);
  // Compare CALENDAR days, not 24h blocks: an interview at 09:00 tomorrow is
  // "tomorrow" whether you ask at 23:00 tonight or at 08:00 in the morning.
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(d) - startOf(now)) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  if (days < 0) return `${Math.abs(days)} days ago`;
  return `in ${days} days`;
}

export const timeOf = (at) =>
  new Date(at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

/** The window both readers share. */
export const SCHEDULED_DAYS = 60;

/**
 * T415 — every row leads to the application it belongs to.
 *
 * "Open the application" used to go to a bare `/applications`, so the answer
 * to "which one?" was for him to find it again in a list of a hundred. The
 * tracker reads `?focus=` and opens that row's drawer.
 */
export function focusHref(recId, open = null) {
  const q = new URLSearchParams({ focus: String(recId) });
  // T440 — optionally, which of that application's tools to open on arrival.
  if (open) q.set('open', open);
  return `/applications?${q}`;
}

function Row({ e, onCleared }) {
  const [busy, setBusy] = useState(false);

  const clear = async () => {
    setBusy(true);
    try {
      // `e.id` IS the recommendation id — GET /recommendations/upcoming
      // returns the row's own _id under that name and has no
      // `recommendationId` field. A fixture that invented one is how this
      // shipped sending PATCH /recommendations/undefined.
      await api.patch(`/recommendations/${e.id}`, { nextEvent: null });
      onCleared?.(e.id);
      toast('Cleared.', { kind: 'success' });
    } catch (err) {
      toast(err.body?.message || 'Could not clear that.', { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      className={
        'rounded-md border px-3 py-2 ' +
        (e.overdue ? 'border-amber-800/60 bg-amber-950/20' : 'border-slate-800 bg-slate-950/40')
      }
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className={'w-24 shrink-0 font-mono text-xs ' + (e.overdue ? 'text-amber-400' : 'text-slate-400')}>
          {relativeDay(e.at)}
        </span>
        <span className="w-14 shrink-0 font-mono text-xs text-slate-500">{timeOf(e.at)}</span>
        <span className="min-w-0 flex-1">
          <span className="text-sm text-slate-200">{KIND_LABEL[e.kind] ?? 'Event'}</span>
          <span className="text-sm text-slate-500"> — {e.job?.company || 'unknown company'}</span>
          <span className="block truncate text-xs text-slate-500">{e.job?.title}</span>
          {e.note && <span className="mt-0.5 block text-xs text-slate-400">{e.note}</span>}
        </span>
      </div>
      {onCleared && (
        <div className="mt-1.5 flex flex-wrap gap-3">
          <Link
            to={focusHref(e.id)}
            className="text-[11px] text-slate-400 underline decoration-dotted transition hover:text-brand-400"
          >
            Open the application
          </Link>
          <button
            type="button"
            disabled={busy}
            onClick={clear}
            className="text-[11px] text-slate-500 underline decoration-dotted transition hover:text-slate-300 disabled:opacity-50"
          >
            Done — clear it
          </button>
        </div>
      )}
    </li>
  );
}

/**
 * @param {object}   props
 * @param {number}   [props.limit]     how many rows to render (the dashboard shows a few)
 * @param {boolean}  [props.actions]   render Open/Clear per row (the Tasks page does)
 * @param {Function} [props.onCount]   report the TOTAL, not the rendered slice
 */
const ScheduledList = forwardRef(function ScheduledList({ limit, actions = false, onCount }, ref) {
  const [events, setEvents] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.get(`/recommendations/upcoming?days=${SCHEDULED_DAYS}`);
      const list = d?.events ?? [];
      setEvents(list);
      setFailed(false);
      onCount?.(list.length);
    } catch {
      setEvents([]);
      setFailed(true);
      onCount?.(0);
    }
  }, [onCount]);

  useImperativeHandle(ref, () => ({ reload: load }), [load]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const drop = (id) => setEvents((prev) => (prev ?? []).filter((x) => x.id !== id));

  if (events === null) return null;

  const overdue = events.filter((e) => e.overdue);
  const ahead = events.filter((e) => !e.overdue);
  const ordered = [...overdue, ...ahead];
  const shown = limit ? ordered.slice(0, limit) : ordered;

  if (!events.length) {
    // T387 — an empty section and a broken one are different sentences.
    // Nothing at all on the dashboard, because an empty card there teaches
    // you to stop reading that part of the screen.
    if (!actions) return null;
    return (
      <section className="space-y-2">
        <h2 className="text-sm font-medium text-brand-500">Scheduled</h2>
        <p className="max-w-prose text-xs text-slate-500">
          {failed
            ? 'Could not load what is scheduled just now.'
            : 'Nothing scheduled. Interview and screening dates you record appear here.'}
        </p>
      </section>
    );
  }

  return (
    <section className={actions ? 'space-y-2' : 'rounded-lg border border-slate-800 bg-slate-900/40 p-4'}>
      <div className="flex items-baseline justify-between gap-2">
        {actions ? (
          <h2 className="text-sm font-medium text-brand-500">Scheduled</h2>
        ) : (
          <Link
            to="/tasks"
            className="text-sm font-semibold text-slate-100 underline decoration-dotted hover:text-brand-400"
          >
            What&rsquo;s next
          </Link>
        )}
        {overdue.length > 0 && (
          <span className="rounded-full border border-amber-800 bg-amber-950/20 px-2 py-0.5 text-[11px] text-amber-300">
            {overdue.length} overdue
          </span>
        )}
      </div>

      <ul className={actions ? 'space-y-1.5' : 'mt-3 space-y-2'}>
        {shown.map((e) => (
          <Row key={e.id} e={e} onCleared={actions ? drop : undefined} />
        ))}
      </ul>

      {/* T408's rule, applied here: a list that is a page says so. */}
      {shown.length < ordered.length && (
        <Link
          to="/tasks"
          className="mt-2 inline-block text-[11px] text-slate-500 underline decoration-dotted transition hover:text-brand-400"
        >
          {ordered.length - shown.length} more scheduled →
        </Link>
      )}
    </section>
  );
});

export default ScheduledList;
